import { useEffect, useState } from "react";
import { getSharedUscStandaloneQueryParams } from "../../shared-logic/utils/uscUtils";
import { useUscSelector } from "../redux/store";
import { removeLastSlashAndWhiteSpace } from "../../../common-deprecated/utils";
import { isUscRetailerLandingPdp } from "../utils/pdpUtils";

// We intentionally don't use the common constant here as that includes .html
const AEM_AUTHOR_URL_SUFFIX = "?wcmmode=disabled";

/**
 * Redirects to the car-filter
 * If the users previous page was the car-filter, he will get the same filters config.
 */
const useBackToCarFilterUrl = (): string => {
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const vehicle = useUscSelector((state) => state.vehicle);

    const { landingPageUrl } = uscSettings;

    const [url, setUrl] = useState("");

    useEffect(() => {
        const previousUrl = document.referrer;
        const currentUrlOrigin = window.location.origin; // e.g. https://www.toyota.co.uk

        if (previousUrl) {
            let urlToSet = "";

            if (!commonSettings.isStandalone && shouldUsePreviousUrl(previousUrl, currentUrlOrigin))
                urlToSet = getAmendedPreviousUrl(previousUrl);

            // If the url is still empty, set the default URL
            if (!urlToSet) urlToSet = getCarFilterUrl();

            setUrl(urlToSet);
        } else {
            // No previous url found, set the default URL
            // This can happen when the user enters the page directly, for example by clicking on a
            // search result, or when the user enters the page by typing the URL in the browser
            setUrl(getCarFilterUrl());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * We should only use the previousUrl if the user came from the car-filter,
     * or if we're on a USC Retailer Landing-specific PDP in which case we check
     * the origin of the current URL to make sure we're on the same domain.
     */
    const shouldUsePreviousUrl = (previousUrl: string, currentUrlOrigin: string): boolean => {
        // document.referrer is the full URL (including query params) of the previous page
        // Concatenating pathname and origin gives us the full URL without query params, which is what we want to compare
        const { pathname, origin } = new URL(previousUrl);
        const previousUrlPath = removeLastSlashAndWhiteSpace(origin + pathname);

        const cameFromCarFilter = previousUrlPath === landingPageUrl.replace(AEM_AUTHOR_URL_SUFFIX, "");
        const isOnRetailerPdp = previousUrl.includes(currentUrlOrigin) && isUscRetailerLandingPdp(uscSettings);

        return cameFromCarFilter || isOnRetailerPdp;
    };

    const getCarFilterUrl = (): string => {
        const { isStandalone, country, language } = commonSettings;
        if (isStandalone) {
            const sharedQueryParams = getSharedUscStandaloneQueryParams(commonSettings);
            return `/${country}/${language}/car-filter?carFilter=${vehicle.type}${sharedQueryParams}`;
        } else {
            const separator = landingPageUrl.includes("?") ? "&" : "?";
            return `${landingPageUrl}${separator}scrollToCarFilter=true`;
        }
    };

    const getAmendedPreviousUrl = (previousUrl: string): string => {
        if (previousUrl.includes("scrollToCarFilter=true")) {
            return previousUrl;
        } else {
            const separator = previousUrl.includes("?") ? "&" : "?";
            return `${previousUrl}${separator}scrollToCarFilter=true`;
        }
    };

    return url;
};

export default useBackToCarFilterUrl;
